import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";

/**
 * Workshop: FrameErrorState — inline error display rendered inside a
 * frame body when execution fails.
 *
 * Shows error icon, "Execution Failed" heading, error message text
 * (truncated with expand toggle), Retry and Dismiss buttons.
 *
 * Meant to replace the frame's normal content when status === "failed".
 */

interface FrameErrorStateProps {
  errorMessage: string;
  onRetry: () => void;
  onDismiss: () => void;
}

/** Max characters before truncation. */
const TRUNCATE_LENGTH = 200;

export function FrameErrorState({
  errorMessage,
  onRetry,
  onDismiss,
}: FrameErrorStateProps) {
  const [expanded, setExpanded] = useState(false);
  const isLong = errorMessage.length > TRUNCATE_LENGTH;
  const displayMessage =
    isLong && !expanded
      ? errorMessage.slice(0, TRUNCATE_LENGTH) + "..."
      : errorMessage;

  return (
    <div
      className="flex flex-col gap-2 py-1"
      data-testid="frame-error-state"
    >
      {/* Heading */}
      <div className="flex items-center gap-1.5">
        <Icons.XCircle className="h-3.5 w-3.5 shrink-0 text-danger" />
        <span className="text-canvas-base font-medium text-danger">
          Execution Failed
        </span>
      </div>

      {/* Error message */}
      <div
        className={cn(
          "rounded-md border border-danger/20 bg-danger-muted px-2.5 py-2",
          "text-canvas-sm leading-relaxed text-secondary",
        )}
        data-testid="frame-error-state-message"
      >
        <span className="whitespace-pre-wrap break-words">{displayMessage}</span>
        {isLong && (
          <button
            type="button"
            className="ml-1 text-canvas-sm font-medium text-muted transition-colors hover:text-primary"
            onClick={() => setExpanded((e) => !e)}
            data-testid="frame-error-state-expand"
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <Button
          variant="secondary"
          size="sm"
          onClick={onRetry}
          data-testid="frame-error-state-retry"
        >
          <Icons.RotateCcw className="h-3 w-3" />
          Retry
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          data-testid="frame-error-state-dismiss"
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}

export type { FrameErrorStateProps };
